import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { canManageClientInvoices, canViewClientInvoices } from '../utils/firmAccess';
import InvoicePreview from './InvoicePreview';

interface ClientInvoice {
  id: number;
  client_firm_id: number;
  invoice_number: string;
  invoice_date: string;
  period_start: string;
  period_end: string;
  opening_balance: number;
  total_billing: number;
  total_payments: number;
  net_amount: number;
  currency: string;
  status: 'draft' | 'submitted';
  notes: string;
  total_paid: number;
  remaining_balance: number;
  payment_status: 'unpaid' | 'partial' | 'paid';
  invoice_scope?: 'firm' | 'case';
  case_id?: number | null;
  case?: { id: number; title: string } | null;
  clientFirm: {
    id: number;
    name: string;
  };
  createdBy: {
    id: number;
    name: string;
  };
}

interface ClientFirm {
  id: number;
  name: string;
  type: string;
}

interface ClientEstimate {
  id: number;
  case_id: number;
  case: {
    id: number;
    title: string;
    subtitle: string;
    project: {
      id: number;
      name: string;
    };
  };
  billing_type: string;
  rate: number;
  quantity: number;
  total_amount: number;
  description: string;
  approved_at: string;
}

const toLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthRange = (year: number, month: number): { period_start: string; period_end: string } => ({
  period_start: toLocalDateString(new Date(year, month - 1, 1)),
  period_end: toLocalDateString(new Date(year, month, 0)),
});

const ClientInvoices: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = canManageClientInvoices(user);
  const canView = canViewClientInvoices(user);
  const previewRequestId = useRef(0);
  const [invoices, setInvoices] = useState<ClientInvoice[]>([]);
  const [clientFirms, setClientFirms] = useState<ClientFirm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [filters, setFilters] = useState({
    client_firm_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    status: '',
  });

  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [availableEstimates, setAvailableEstimates] = useState<ClientEstimate[]>([]);
  const [selectedEstimates, setSelectedEstimates] = useState<number[]>([]);
  const [timerEntries, setTimerEntries] = useState<any[]>([]);
  const [previewFetched, setPreviewFetched] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    client_firm_id: '',
    invoice_scope: 'firm' as 'firm' | 'case',
    case_id: '',
    ...getMonthRange(new Date().getFullYear(), new Date().getMonth() + 1),
    notes: '',
  });
  const [generateLoading, setGenerateLoading] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (user && !canView) {
      navigate('/', { replace: true });
      return;
    }
    loadInvoices();
    if (canManage) {
      loadClientFirms();
    }
  }, [filters, user]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.client_firm_id) params.append('client_firm_id', filters.client_firm_id);
      if (filters.month) params.append('month', filters.month.toString());
      if (filters.year) params.append('year', filters.year.toString());
      if (filters.status) params.append('status', filters.status);

      const response = await api.get(`/client-invoices?${params}`);
      if (response.data.success) {
        setInvoices(response.data.data.data || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadClientFirms = async () => {
    try {
      const response = await api.get('/firms?type=client');
      if (response.data.success) {
        setClientFirms(response.data.data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to load client firms:', err);
    }
  };

  const loadAvailableEstimates = async (form: typeof generateForm) => {
    const requestId = ++previewRequestId.current;
    try {
      setGenerateLoading(true);
      setPreviewFetched(false);
      const params = new URLSearchParams({
        client_firm_id: form.client_firm_id,
        period_start: form.period_start,
        period_end: form.period_end,
      });
      if (form.invoice_scope === 'case' && form.case_id) {
        params.append('case_id', form.case_id);
      }

      const estimatesResponse = await api.get(`/client-estimates/invoiceable?${params}`);
      if (requestId !== previewRequestId.current) {
        return;
      }
      const estimates = estimatesResponse.data.success ? estimatesResponse.data.data : [];
      setAvailableEstimates(estimates);
      setSelectedEstimates(estimates.map((est: ClientEstimate) => est.id));

      const previewPayload: any = {
        client_firm_id: form.client_firm_id,
        period_start: form.period_start,
        period_end: form.period_end,
        estimate_ids: estimates.map((est: ClientEstimate) => est.id),
      };
      if (form.invoice_scope === 'case' && form.case_id) {
        previewPayload.case_id = form.case_id;
      }

      const previewResponse = await api.post('/client-invoices/preview', previewPayload);

      if (requestId !== previewRequestId.current) {
        return;
      }
      if (previewResponse.data.success) {
        setTimerEntries(previewResponse.data.data.timer_entries || []);
      } else {
        setTimerEntries([]);
      }
      setPreviewFetched(true);
    } catch (err: any) {
      if (requestId !== previewRequestId.current) {
        return;
      }
      setError(err.response?.data?.message || 'Failed to load available items');
      setAvailableEstimates([]);
      setSelectedEstimates([]);
      setTimerEntries([]);
      setPreviewFetched(true);
    } finally {
      if (requestId === previewRequestId.current) {
        setGenerateLoading(false);
      }
    }
  };

  const handleGenerateDialogOpen = () => {
    const range = getMonthRange(Number(filters.year), Number(filters.month));
    const clientFirmId = filters.client_firm_id || '';
    const nextForm = {
      client_firm_id: clientFirmId,
      invoice_scope: 'firm' as const,
      case_id: '',
      period_start: range.period_start,
      period_end: range.period_end,
      notes: '',
    };
    setShowGenerateDialog(true);
    setGenerateForm(nextForm);
    setAvailableEstimates([]);
    setSelectedEstimates([]);
    setTimerEntries([]);
    setPreviewFetched(false);
    if (clientFirmId) {
      loadAvailableEstimates(nextForm);
    }
  };

  const handleGenerateFormChange = (field: string, value: string) => {
    const updatedForm = { ...generateForm, [field]: value };
    if (field === 'invoice_scope' && value === 'firm') {
      updatedForm.case_id = '';
    }
    if (field === 'client_firm_id') {
      updatedForm.case_id = '';
    }
    setGenerateForm(updatedForm);

    if (updatedForm.client_firm_id && updatedForm.period_start && updatedForm.period_end) {
      loadAvailableEstimates(updatedForm);
    } else {
      setAvailableEstimates([]);
      setSelectedEstimates([]);
      setTimerEntries([]);
      setPreviewFetched(false);
    }
  };

  const buildGeneratePayload = () => {
    const payload: any = {
      client_firm_id: generateForm.client_firm_id,
      period_start: generateForm.period_start,
      period_end: generateForm.period_end,
      notes: generateForm.notes,
    };
    if (generateForm.invoice_scope === 'case' && generateForm.case_id) {
      payload.case_id = generateForm.case_id;
    }
    if (selectedEstimates.length > 0) {
      payload.estimate_ids = selectedEstimates;
    }
    return payload;
  };

  const handlePreviewInvoice = async () => {
    if (!generateForm.client_firm_id) {
      setError('Please select a client firm');
      return;
    }
    if (generateForm.invoice_scope === 'case' && !generateForm.case_id) {
      setError('Please select a case for case-wise invoicing');
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await api.post('/client-invoices/preview', buildGeneratePayload());
      if (response.data.success) {
        setPreviewData(response.data.data);
        setShowPreviewDialog(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load invoice preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewExisting = async (invoice: ClientInvoice) => {
    try {
      setPreviewLoading(true);
      const response = await api.get(`/client-invoices/${invoice.id}`);
      if (response.data.success) {
        const data = response.data.data;
        if (!data.clientFirm && data.client_firm) {
          data.clientFirm = data.client_firm;
        }
        data.case_title = data.case?.title || data.case_title;
        setPreviewData(data);
        setShowPreviewDialog(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load invoice preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!generateForm.client_firm_id) {
      setError('Please select a client firm');
      return;
    }
    if (generateForm.invoice_scope === 'case' && !generateForm.case_id) {
      setError('Please select a case for case-wise invoicing');
      return;
    }

    try {
      setGenerateLoading(true);
      const response = await api.post('/client-invoices/generate', buildGeneratePayload());

      if (response.data.success) {
        setSuccess('Client invoice generated successfully');
        setShowGenerateDialog(false);
        setShowPreviewDialog(false);
        setSelectedEstimates([]);
        loadInvoices();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate invoice');
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleToggleEstimate = (estimateId: number) => {
    setSelectedEstimates(prev =>
      prev.includes(estimateId)
        ? prev.filter(id => id !== estimateId)
        : [...prev, estimateId]
    );
  };

  const handleViewInvoice = (invoiceId: number) => {
    navigate(`/client-invoices/${invoiceId}`);
  };

  const handleDownloadPdf = async (invoice: ClientInvoice) => {
    try {
      const response = await api.get(`/client-invoices/${invoice.id}/download`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `client-invoice-${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to download invoice PDF');
    }
  };

  const handleSubmitInvoice = async (invoice: ClientInvoice) => {
    if (!window.confirm('Are you sure you want to submit this invoice to the client?')) {
      return;
    }

    try {
      const response = await api.post(`/client-invoices/${invoice.id}/submit`);
      if (response.data.success) {
        setSuccess('Invoice submitted to client successfully');
        loadInvoices();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit invoice');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'default',
      submitted: 'success',
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      unpaid: 'error',
      partial: 'warning',
      paid: 'success',
    };
    return colors[status] || 'default';
  };

  const availableCases = useMemo(() => {
    const map = new Map<number, { id: number; title: string }>();
    availableEstimates.forEach((est) => {
      if (est.case_id) {
        map.set(est.case_id, { id: est.case_id, title: est.case?.title || `Case ${est.case_id}` });
      }
    });
    timerEntries.forEach((entry: any) => {
      if (entry.case_id && !map.has(entry.case_id)) {
        map.set(entry.case_id, { id: entry.case_id, title: entry.case_title || `Case ${entry.case_id}` });
      }
    });
    return Array.from(map.values());
  }, [availableEstimates, timerEntries]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Client Invoices</Typography>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleGenerateDialogOpen}
          >
            Generate Invoice
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {canManage && (
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Client Firm</InputLabel>
            <Select
              value={filters.client_firm_id}
              onChange={(e) => setFilters({ ...filters, client_firm_id: e.target.value })}
              label="Client Firm"
            >
              <MenuItem value="">All Firms</MenuItem>
              {clientFirms.map((firm) => (
                <MenuItem key={firm.id} value={firm.id}>
                  {firm.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          )}

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Month</InputLabel>
            <Select
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: Number(e.target.value) })}
              label="Month"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                <MenuItem key={month} value={month}>
                  {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: Number(e.target.value) })}
              label="Year"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {canManage && (
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="submitted">Submitted</MenuItem>
            </Select>
          </FormControl>
          )}

          <IconButton onClick={loadInvoices} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Invoices Table */}
      <Paper elevation={2}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : invoices.length === 0 ? (
          <Box p={4}>
            <Typography color="text.secondary" align="center">
              {canManage
                ? 'No invoices found. Click "Generate Invoice" to create one.'
                : 'No invoices are ready to view yet.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice Number</TableCell>
                  <TableCell>Client Firm</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.invoice_number}</TableCell>
                    <TableCell>{invoice.clientFirm?.name}</TableCell>
                    <TableCell>
                      {invoice.case_id
                        ? invoice.case?.title || `Case #${invoice.case_id}`
                        : 'All cases'}
                    </TableCell>
                    <TableCell>
                      {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                    </TableCell>
                    <TableCell>${Number(invoice.net_amount).toFixed(2)}</TableCell>
                    <TableCell>${Number(invoice.total_paid).toFixed(2)}</TableCell>
                    <TableCell>${Number(invoice.remaining_balance).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status.toUpperCase()}
                        color={getStatusColor(invoice.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.payment_status.toUpperCase()}
                        color={getPaymentStatusColor(invoice.payment_status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Preview Invoice">
                          <IconButton
                            size="small"
                            onClick={() => handlePreviewExisting(invoice)}
                            disabled={previewLoading}
                          >
                            <PreviewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewInvoice(invoice.id)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadPdf(invoice)}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        {canManage && invoice.status === 'draft' && (
                          <Tooltip title="Submit to Client">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSubmitInvoice(invoice)}
                            >
                              <SendIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Generate Invoice Dialog */}
      <Dialog
        open={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Generate Client Invoice</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Client Firm</InputLabel>
            <Select
              value={generateForm.client_firm_id}
              onChange={(e) => handleGenerateFormChange('client_firm_id', e.target.value)}
              label="Client Firm"
              required
            >
              {clientFirms.map((firm) => (
                <MenuItem key={firm.id} value={firm.id}>
                  {firm.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <FormLabel>Invoice Type</FormLabel>
            <RadioGroup
              row
              value={generateForm.invoice_scope}
              onChange={(e) => handleGenerateFormChange('invoice_scope', e.target.value)}
            >
              <FormControlLabel value="firm" control={<Radio />} label="All cases (firm invoice)" />
              <FormControlLabel value="case" control={<Radio />} label="Case-wise invoice" />
            </RadioGroup>
          </FormControl>

          {generateForm.invoice_scope === 'case' && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Case</InputLabel>
              <Select
                value={generateForm.case_id}
                onChange={(e) => handleGenerateFormChange('case_id', e.target.value as string)}
                label="Case"
                required
              >
                {availableCases.map((caseItem) => (
                  <MenuItem key={caseItem.id} value={String(caseItem.id)}>
                    {caseItem.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label="Period Start"
            type="date"
            value={generateForm.period_start}
            onChange={(e) => handleGenerateFormChange('period_start', e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            fullWidth
            label="Period End"
            type="date"
            value={generateForm.period_end}
            onChange={(e) => handleGenerateFormChange('period_end', e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />

          {generateLoading && (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          )}

          {availableEstimates.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Available Estimates ({selectedEstimates.length} selected)
              </Typography>
              <List>
                {availableEstimates.map((estimate) => (
                  <ListItem key={estimate.id} dense>
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedEstimates.includes(estimate.id)}
                        onChange={() => handleToggleEstimate(estimate.id)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${estimate.case?.title || 'Case'} - ${estimate.case?.project?.name || ''}`}
                      secondary={`${estimate.quantity} ${estimate.billing_type.replace('_', ' ')} @ $${Number(estimate.rate).toFixed(2)} = $${Number(estimate.total_amount).toFixed(2)}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Timer Entries Preview */}
          {timerEntries.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Unbilled Timer Entries (Automatically Included)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Case Title</strong></TableCell>
                      <TableCell align="right"><strong>Hours</strong></TableCell>
                      <TableCell align="right"><strong>Rate</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timerEntries.map((entry: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{entry.case_title}</TableCell>
                        <TableCell align="right">
                          {entry.hours.toFixed(4)} hrs
                          <Typography variant="caption" display="block" color="text.secondary">
                            ({entry.time_display})
                          </Typography>
                        </TableCell>
                        <TableCell align="right">${entry.rate.toFixed(2)}/hr</TableCell>
                        <TableCell align="right"><strong>${entry.amount.toFixed(2)}</strong></TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} align="right"><strong>Total Timer Entries:</strong></TableCell>
                      <TableCell align="right">
                        <strong>${timerEntries.reduce((sum: number, e: any) => sum + e.amount, 0).toFixed(2)}</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {previewFetched && availableEstimates.length === 0 && timerEntries.length === 0 && !generateLoading && generateForm.client_firm_id && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No approved estimates or unbilled billable timer entries found for the selected period.
              The Monthly Case Billability Report defaults to last month — make sure this invoice period matches that report.
            </Alert>
          )}
          
          {availableEstimates.length > 0 && timerEntries.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Timer entries shown above will be automatically included in the invoice along with selected estimates.
            </Alert>
          )}

          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={generateForm.notes}
            onChange={(e) => setGenerateForm({ ...generateForm, notes: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
          <Button
            onClick={handlePreviewInvoice}
            variant="outlined"
            disabled={
              generateLoading ||
              previewLoading ||
              !generateForm.client_firm_id ||
              (generateForm.invoice_scope === 'case' && !generateForm.case_id) ||
              (previewFetched && availableEstimates.length === 0 && timerEntries.length === 0)
            }
          >
            {previewLoading ? <CircularProgress size={24} /> : 'Preview'}
          </Button>
          <Button
            onClick={handleGenerateInvoice}
            variant="contained"
            disabled={
              generateLoading ||
              !generateForm.client_firm_id ||
              (generateForm.invoice_scope === 'case' && !generateForm.case_id) ||
              (previewFetched && availableEstimates.length === 0 && timerEntries.length === 0)
            }
          >
            {generateLoading ? <CircularProgress size={24} /> : 'Generate Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      <InvoicePreview
        open={showPreviewDialog}
        onClose={() => setShowPreviewDialog(false)}
        invoice={previewData}
        onGenerate={canManage && showGenerateDialog ? handleGenerateInvoice : undefined}
      />

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientInvoices;

